import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  CATEGORY_TREE,
  CATEGORY_SUGGESTIONS,
  POPULAR_SEARCHES,
  SERVICE_KEYWORDS
} from '../../data/categoryStructure';

const useQuery = () => new URLSearchParams(useLocation().search);

const Search: React.FC = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const initialQuery = query.get('q') || '';
  const [searchTerm, setSearchTerm] = useState(initialQuery);

  const trimmedSearch = searchTerm.trim();

  const results = useMemo(() => {
    if (!trimmedSearch) return [];
    const normalized = trimmedSearch.toLowerCase();
    const categoryMatches = CATEGORY_TREE.flatMap((category) => [
      category,
      ...(category.children || [])
    ]).filter((item) => item.name.toLowerCase().includes(normalized));
    return Array.from(new Map(categoryMatches.map((item) => [item.path, item])).values());
  }, [trimmedSearch]);

  

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    navigate(`/search?q=${encodeURIComponent(searchTerm)}`);
  };

  return (
    <section className="search-page">
      <div className="search-page__hero">
        <h1>Search the enterprise catalog</h1>
        <p>Find products, services, categories and B2B solutions across our full site.</p>
        <form className="search-page__form" onSubmit={onSubmit}>
          <input
            type="search"
            placeholder="Search products, categories or services"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            className="search-page__input"
          />
          <button type="submit" className="search-page__button">Search</button>
        </form>
      </div>

      {trimmedSearch ? (
        <div className="search-page__results">
          <h2>Matching categories</h2>
          {results.length ? (
            <ul className="search-page__result-list">
              {results.map((item) => (
                <li key={item.path} className="search-page__result-item">
                  <a href={item.path}>{item.name}</a>
                </li>
              ))}
            </ul>
          ) : (
            <p>No matching categories found for “{trimmedSearch}”. Try broader terms.</p>
          )}
        </div>
      ) : (
        <div className="search-page__quick-links">
          <div className="search-page__section">
            <h2>Popular searches</h2>
            <div className="search-page__chips">
              {POPULAR_SEARCHES.map((term) => (
                <button
                  key={term}
                  className="search-page__chip"
                  onClick={() => {
                    setSearchTerm(term);
                    navigate(`/search?q=${encodeURIComponent(term)}`);
                  }}
                >
                  {term}
                </button>
              ))}
            </div>
          </div>
          <div className="search-page__section">
            <h2>Explore categories</h2>
            <div className="search-page__chips">
              {CATEGORY_SUGGESTIONS.slice(0, 8).map((item) => (
                <button
                  key={item.path}
                  className="search-page__chip"
                  onClick={() => navigate(item.path)}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
          <div className="search-page__section">
            <h2>Service links</h2>
            <div className="search-page__chips">
              {SERVICE_KEYWORDS.map((item) => (
                <button
                  key={item.path}
                  className="search-page__chip"
                  onClick={() => navigate(item.path)}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Search;
